/**
 * LocationRegionField — the Region control on the Create Workshop drawer's
 * Location step, which renders one of five ways depending on what the viewer
 * may write to.
 *
 * `fixed` is set by the launching page and shown as text. Otherwise the field
 * follows the writable-regions answer: still loading says so rather than
 * offering a list it may contradict a moment later; a failed lookup says the
 * regions could not be loaded, which is a different fact from having none; no
 * writable regions says that outright, because an empty dropdown reads as a
 * broken control; a single writable region is a value, not a choice; anything
 * more is the tree select.
 *
 * Its own file because each module declares one component
 * (`react/no-multi-comp`).
 */
import type { RegionNode } from '../../../api/regions';
import RegionTreeSelect from '../../../shared/RegionTreeSelect';
import { soleRegion } from '../workshops/create/workshopCreateRegions';

import { ControlBlock } from './ControlBlock';
import { FixedField } from './FixedField';

export function LocationRegionField({
  mode,
  regions,
  loading,
  failed,
  value,
  label,
  error,
  onChange,
}: {
  /** `fixed` renders read-only text; anything else is driven by `regions`. */
  mode: 'fixed' | 'prefill' | 'open';
  regions: RegionNode[];
  loading: boolean;
  /** The regions could not be loaded, so `regions` being empty means unknown. */
  failed: boolean;
  value: string;
  /** Resolved name of the selected region, for the read-only renderings. */
  label: string;
  error?: string;
  onChange: (regionId: string) => void;
}) {
  if (mode === 'fixed') {
    return <FixedField label="Region" value={label} ariaLabel="Region (fixed)" />;
  }
  if (loading) {
    return (
      <FixedField
        label="Region"
        value="Loading regions…"
        ariaLabel="Region (loading)"
        error={error}
      />
    );
  }
  if (failed) {
    return (
      <FixedField
        label="Region"
        value="Couldn’t load regions"
        ariaLabel="Region (unavailable)"
        error={error}
      />
    );
  }
  if (regions.length === 0) {
    return (
      <FixedField
        label="Region"
        value="No regions available"
        ariaLabel="Region (none)"
        error={error}
      />
    );
  }
  const only = soleRegion(regions);
  if (only) {
    return <FixedField label="Region" value={only.name} ariaLabel="Region (fixed)" error={error} />;
  }
  return (
    <ControlBlock label="Region" error={error}>
      <RegionTreeSelect
        regions={regions}
        value={value}
        onChange={onChange}
        placeholder="Select a region…"
        variant="dashboards"
      />
    </ControlBlock>
  );
}
